/**
 * delivery-template-row: the row's chip, subject, header tail and notice. Every word comes from rowWording
 * (delivery-row-wording.ts, CC 0450); this file only lays it out.
 */
import { Banner, Pill, T } from "@v0y4ge/jrni-ui";
import { t as i18nT } from "../../lib/i18n";
import { DELIV_EVENTS, delivStatus, isTemplateShown, stopReasonKnown, stopReasonOf, type DeliveryRow } from "./delivery-data";
import { rowWording, type RowWording } from "./delivery-row-wording";

/** The row's wording. The reason is passed only where the row's events carry it (the drawer). */
export const wordingFor = (row: DeliveryRow, withReason = false): RowWording =>
  rowWording(delivStatus(row), row.contentSource, {
    reason: withReason && stopReasonKnown(row) ? stopReasonOf(row) : null,
    sentAt: row.sent || null, plannedAt: row.plannedAt || null,
  });

/** The content chip. `dedupe` hides it where the status badge beside it already says the same word (the drawer header). */
export function RowChip({ row, dedupe = false }: { row: DeliveryRow; dedupe?: boolean }) {
  if (!row.contentSource) return null;
  const w = wordingFor(row);
  if (dedupe && (DELIV_EVENTS[w.status]?.label ?? "") === w.chip.label) return null;
  return <span data-testid="delivery-row-chip" data-status={w.status} data-claim={w.claim}><Pill tone={w.chip.tone}>{w.chip.label}</Pill></span>;
}

/** The drawer header's tail after the channel name. */
export function RowHeaderTail({ row }: { row: DeliveryRow }) {
  return <span data-testid="delivery-row-header">{wordingFor(row).header}</span>;
}

/** The subject (or text) line. A template row reads as the template, in code type. The reason line shows only
 *  when `withReason` is set and the row's events carry it (the drawer). */
export function DeliverySubject({ row, size = "row", withReason = false }: { row: DeliveryRow; size?: "row" | "title"; withReason?: boolean }) {
  const line = row.subject || row.text || "";
  const reason = withReason ? wordingFor(row, true).reasonLine : null;
  const reasonEl = reason ? <span style={{ display: "block", fontSize: size === "title" ? 13 : 11.5, color: T.soft, whiteSpace: "normal" }} data-testid="delivery-stop-reason">{reason}</span> : null;
  if (!isTemplateShown(row)) {
    return size === "title"
      ? <span style={{ display: "flex", flexDirection: "column", gap: 2 }}><span style={{ display: "block", fontSize: 16.5, fontWeight: 700, color: T.n1, lineHeight: 1.3 }}>{line}</span>{reasonEl}</span>
      : <div style={{ maxWidth: "100%", color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{line}</div>;
  }
  return (
    <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, maxWidth: "100%" }} data-testid="delivery-template-subject">
      <span style={{ fontSize: 11, fontWeight: 600, color: T.soft, textTransform: "uppercase", letterSpacing: "0.04em" }}>{i18nT("messaging.deliveryLog.templateLabel", "Template")}</span>
      <span style={{ fontFamily: T.fontMono, fontSize: size === "title" ? 14 : 12.5, color: T.n2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: size === "title" ? "normal" : "nowrap" }}>{line}</span>
      {reasonEl}
    </span>
  );
}

/** Above the body in the drawer: what the reader is looking at. Template rows only. */
export function TemplateNotice({ row }: { row: DeliveryRow }) {
  const w = wordingFor(row, true);
  if (!w.notice) return null;
  if (w.notice.tone === "neutral") {
    return <div style={{ marginBottom: 10, fontSize: 12.5, color: T.soft }} data-testid="delivery-template-notice" data-status={w.status}>{w.notice.body}</div>;
  }
  return (
    <div style={{ marginBottom: 10 }} data-testid="delivery-template-notice" data-status={w.status}>
      <Banner tone="warning" size="compact" title={w.notice.title ?? undefined}>{w.notice.body}</Banner>
    </div>
  );
}
